import type { NodeV2 } from '@vedalib/editor'
import { isNodeV2 } from '@vedalib/editor'
import * as R from 'ramda'
import React, { useCallback, useMemo } from 'react'

import { safeParseArea } from 'components/Editor/NodeV2/formControls/Area/utils'
import AbsolutePortal from 'components/Editor/components/AbsolutePortal'
import { ABSOLUTE_PORTAL_GAP } from 'components/Editor/components/AbsolutePortal/constants'
import KeyBindTag from 'components/Editor/components/KeyBindTag/KeyBindTag'
import { Tooltip } from 'components/uiKit/Dropdown'
import Icon from 'components/uiKit/Icon'
import { KitSize } from 'components/uiKit/KitTypes'
import { updateBlock } from 'services/Store/Project/actions'
import { AppMode } from 'services/Store/Project/enums'
import { useProjectContext, useProjectDispatch } from 'services/Store/Project/hooks'
import { addGridRows, moveGridArea } from 'services/Store/Project/mutators/node'
import { getIsEditingGrid, mergeNodeStyle } from 'services/Store/Project/selectors'
import type { Block, IBlockMode } from 'services/Store/Project/types'
import { t } from 'services/Translation'

import { useContentPlus } from '../../useContentPlus'
import { ResizeControlActionUiEnum } from '../enums'
import CellResizeControl from './CellResizeControl'
import * as s from './Grid.module.scss'

interface IGridControlsProps {
  mode: IBlockMode
  block: Block
  node: NodeV2
  selected: NodeV2[]
}

const GridControls: React.FC<IGridControlsProps> = (props) => {
  const { block, mode, node, selected } = props
  const dispatch = useProjectDispatch()
  const isPro = mode.editorMode === AppMode.pro
  const isContentPlus = useContentPlus()
  const isRepeatable = node.repeatable
  const isEditingGrid = useProjectContext(getIsEditingGrid, selected[0]?.id)

  const { rowStart, rowEnd } = useMemo(() => {
    const styles = selected.map((node) =>
      mergeNodeStyle(
        node.style,
        mode.deviceMode,
        node.elementId ? block.elements[node.elementId]?.type : undefined,
      ),
    )
    const areas = styles.map((style) => safeParseArea(style.gridArea))
    const rowStart = Math.min(...areas.map((area) => area.rowStart)) || 0
    const rowEnd = Math.max(...areas.map((area) => area.rowEnd)) || 0
    const position = styles.every((style) => style.position === 'absolute')
      ? 'absolute'
      : 'relative'

    return { rowEnd, rowStart, position }
  }, [block.elements, mode.deviceMode, selected])

  const handleAddRowBottom = useCallback(() => {
    if (!node) {
      return
    }
    const row = rowEnd
    const bottomChildren = node?.children
      .filter((id) => {
        const child = block.schema.nodes[id]
        if (!isNodeV2(child)) {
          return false
        }
        const childArea = safeParseArea(mergeNodeStyle(child.style, mode.deviceMode).gridArea)
        return childArea.rowStart >= row
      })
      .map((id) => block.schema.nodes[id])
      .filter(isNodeV2)

    const newBlock = R.compose(
      ...(bottomChildren?.map((child) => moveGridArea(child.id, mode.deviceMode, ['down'])) as [
        (b: Block) => Block,
      ]),
      addGridRows(node.id, mode.deviceMode, 1),
    )(block)
    dispatch(updateBlock({ id: block?.uuid, value: newBlock }))
  }, [dispatch, block, node, mode, rowEnd])

  const handleAddRowTop = useCallback(() => {
    if (!node) {
      return
    }
    const row = rowStart
    const bottomChildren = node?.children
      .filter((id) => {
        const child = block.schema.nodes[id]
        if (!isNodeV2(child)) {
          return false
        }
        const childArea = safeParseArea(mergeNodeStyle(child.style, mode.deviceMode).gridArea)
        return childArea.rowStart >= row
      })
      .map((id) => block.schema.nodes[id])
      .filter(isNodeV2)

    const newBlock = R.compose(
      ...(bottomChildren?.map((child) => moveGridArea(child.id, mode.deviceMode, ['down'])) as [
        (b: Block) => Block,
      ]),
      addGridRows(node.id, mode.deviceMode),
    )(block)
    dispatch(updateBlock({ id: block?.uuid, value: newBlock }))
  }, [dispatch, block, node, mode, rowStart])

  return (
    <>
      {(isPro || isContentPlus) && !isEditingGrid && !isRepeatable && selected.length > 0 && (
        <>
          <CellResizeControl
            block={block}
            mode={mode}
            node={node}
            selected={selected}
            type={ResizeControlActionUiEnum.TOP_LEFT}
          />
          <CellResizeControl
            block={block}
            mode={mode}
            node={node}
            selected={selected}
            type={ResizeControlActionUiEnum.TOP}
          />
          <CellResizeControl
            block={block}
            mode={mode}
            node={node}
            selected={selected}
            type={ResizeControlActionUiEnum.TOP_RIGHT}
          />
          <CellResizeControl
            block={block}
            mode={mode}
            node={node}
            selected={selected}
            type={ResizeControlActionUiEnum.RIGHT}
          />
          <CellResizeControl
            block={block}
            mode={mode}
            node={node}
            selected={selected}
            type={ResizeControlActionUiEnum.BOTTOM_RIGHT}
          />
          <CellResizeControl
            block={block}
            mode={mode}
            node={node}
            selected={selected}
            type={ResizeControlActionUiEnum.BOTTOM}
          />
          <CellResizeControl
            block={block}
            mode={mode}
            node={node}
            selected={selected}
            type={ResizeControlActionUiEnum.BOTTOM_LEFT}
          />
          <CellResizeControl
            block={block}
            mode={mode}
            node={node}
            selected={selected}
            type={ResizeControlActionUiEnum.LEFT}
          />
        </>
      )}
      {isPro && !isEditingGrid && !isRepeatable && (
        <AbsolutePortal
          chain='vertical'
          name='zIndex'
          offset={`${-ABSOLUTE_PORTAL_GAP}px`}
          placement='leftTop'
          translateX='-100%'
          translateY='-100%'
        >
          <Tooltip
            mouseEnterDelay={0.5}
            overlay={
              <div className={s.label}>
                {t('editorV2.grid.addRowTop')} <KeyBindTag bind='ctrl+up' dark />
              </div>
            }
            placement='top'
          >
            <div className={s.gridColActions}>
              <Icon name='add' onClick={handleAddRowTop} size={KitSize.XS} />
            </div>
          </Tooltip>
        </AbsolutePortal>
      )}
      {isPro && !isEditingGrid && !isRepeatable && (
        <AbsolutePortal
          chain='vertical'
          name='zIndex'
          offset={`${ABSOLUTE_PORTAL_GAP}px ${-ABSOLUTE_PORTAL_GAP}px`}
          placement='leftBottom'
          translateX='-100%'
        >
          <Tooltip
            mouseEnterDelay={0.5}
            overlay={
              <div className={s.label}>
                {t('editorV2.grid.addRowBottom')} <KeyBindTag bind='ctrl+down' dark />
              </div>
            }
            placement='bottom'
          >
            <div className={s.gridColActions}>
              <Icon name='add' onClick={handleAddRowBottom} size={KitSize.XS} />
            </div>
          </Tooltip>
        </AbsolutePortal>
      )}
    </>
  )
}

export default React.memo(GridControls)
